// ENG-054: the product-thread UI of the widget. The chip at the top of the chat (which product
// thread is on screen, and the way to the general chat and back), the one-line notice after a
// thread switch, and the intro of an empty product thread. None of it is stored or sent: the intro
// makes no LLM call, and a suggestion click sends an ordinary message (intro_suggestion: true).
// ENG-055: the "Recent (n)" menu in the chip row (other product threads with messages), and the
// chip of a thread picked from it.
// ENG-056: the chip row has two parts only: the chip of the open conversation (a product, or the
// general chat named by its last question) and "Recent (n)" with every other place to go. The
// notice after a switch is no longer shown, only announced to screen readers.
// ENG-067: the intro's ready questions depend on the product type (its group, picked from the product's
// `sourcepage` by utils/readyQuestions.ts out of GET /n8n-config `readyQuestions`), in the site widget and in Genie.

import { Chat20Regular } from "@fluentui/react-icons";
import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";

import { getProductDetails } from "../../api";
import { SiteId } from "../../api/models";
import {
    ConversationThread,
    GENERAL_THREAD_ROW_MAX,
    GENERAL_THREAD_TITLE_MAX,
    generalThreadLabel,
    introPrice,
    productRowParts,
    productRowText,
    RecentProductThread,
    threadCopy,
    ThreadLanguage,
    threadMenuModel,
    threadMessageText,
    ThreadSwitchNotice,
    threadSwitchNoticeText
} from "../../utils/conversationThread";
import { normalizeHostContextField } from "../../utils/hostContext";
import { ReadyQuestionsConfig, readyQuestionsFor } from "../../utils/readyQuestions";

import styles from "./ProductThread.module.css";

// The fields the intro shows (GET /product_details?fields=...). ENG-067: `sourcepage` picks the ready questions.
const INTRO_DETAIL_FIELDS = ["content", "image_url", "pricing", "brand", "sourcepage"];

const formatEuro = (value: number) => new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(value);

// The host's own title for this SKU (hostContext.productTitle), when the host gave one for it.
const hostProductTitle = (sku: string | null): string | undefined => {
    if (!sku) return undefined;
    try {
        const hostContext = (window as any)?.PublicWidgetConfig?.hostContext;
        if (normalizeHostContextField(hostContext?.productSku) !== sku) return undefined;
        return normalizeHostContextField(hostContext?.productTitle, 500);
    } catch {
        return undefined;
    }
};

// The product's title as far as it is known: hostContext.productTitle, else `knownTitle` (ENG-055:
// the one the thread registry remembered), else GET /product_details `content` (client-side cache
// in api.ts); undefined until then.
const useKnownProductTitle = (sku: string | null, siteId: SiteId, knownTitle?: string): string | undefined => {
    const hostTitle = hostProductTitle(sku);
    const [fetched, setFetched] = useState<{ sku: string; title: string } | null>(null);

    useEffect(() => {
        if (!sku || hostTitle || knownTitle) return;
        let cancelled = false;
        getProductDetails(sku, siteId, ["content"])
            .then(details => {
                const title = normalizeHostContextField(details?.content, 500);
                if (!cancelled && title) setFetched({ sku, title });
            })
            .catch(error => console.debug("[product-thread] title lookup failed", { sku, error }));
        return () => {
            cancelled = true;
        };
    }, [sku, siteId, hostTitle, knownTitle]);

    if (!sku) return undefined;
    return hostTitle || knownTitle || (fetched?.sku === sku ? fetched.title : "") || undefined;
};

// The product's title, else the SKU itself.
export const useProductTitle = (sku: string | null, siteId: SiteId, knownTitle?: string): string => {
    const title = useKnownProductTitle(sku, siteId, knownTitle);
    return sku ? title || sku : "";
};

// ENG-056: one row of the "Recent" menu, as the bar builds it from threadMenuModel.
interface ThreadMenuRow {
    key: string;
    // ENG-068: a product row with a title is one flowing text "{sku} · {label}" (up to two lines); the SKU is
    // the small part in its own colour. Absent when the label is the SKU: the SKU alone, one line.
    sku?: string;
    label: string;
    // ENG-058: the general chat's row: the chat icon before its label, which (a message) may take two lines.
    general?: boolean;
    title: string;
    ariaLabel: string;
    onChoose: () => void;
}

interface RecentThreadsMenuProps {
    items: ThreadMenuRow[];
    exampleLanguage: ThreadLanguage;
    disabled: boolean;
}

// ENG-055: "Recent (n)", a menu button. Opens on click with focus on the first row; Escape, a
// click outside (or focus leaving it) and a choice close it. Arrow keys, Home and End move
// between the rows. ENG-056: the rows are every other conversation, general chat included.
const RecentThreadsMenu = ({ items, exampleLanguage, disabled }: RecentThreadsMenuProps) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const menuId = useId();
    const copy = threadCopy(exampleLanguage);
    const isOpen = open && !disabled && items.length > 0;

    // An answer started (or the list emptied): close for good, not only until it ends.
    useEffect(() => {
        if (disabled || items.length === 0) setOpen(false);
    }, [disabled, items.length]);

    useEffect(() => {
        if (!isOpen) return;
        itemRefs.current[0]?.focus();
        const onOutside = (event: Event) => {
            if (rootRef.current && !event.composedPath().includes(rootRef.current)) setOpen(false);
        };
        document.addEventListener("pointerdown", onOutside, true);
        document.addEventListener("focusin", onOutside, true);
        return () => {
            document.removeEventListener("pointerdown", onOutside, true);
            document.removeEventListener("focusin", onOutside, true);
        };
    }, [isOpen]);

    const close = (refocus: boolean) => {
        setOpen(false);
        if (refocus) triggerRef.current?.focus();
    };

    const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Escape" && isOpen) {
            event.preventDefault();
            close(true);
        } else if (event.key === "ArrowDown" && !disabled) {
            event.preventDefault();
            setOpen(true);
        }
    };

    const onItemKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const last = items.length - 1;
        const move = (to: number) => {
            event.preventDefault();
            itemRefs.current[to]?.focus();
        };
        if (event.key === "Escape") {
            event.preventDefault();
            close(true);
        } else if (event.key === "ArrowDown") move(index >= last ? 0 : index + 1);
        else if (event.key === "ArrowUp") move(index <= 0 ? last : index - 1);
        else if (event.key === "Home") move(0);
        else if (event.key === "End") move(last);
    };

    return (
        <div className={styles.recent} ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.chipButton} ${styles.recentButton}`}
                onClick={() => setOpen(value => !value)}
                onKeyDown={onTriggerKeyDown}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                aria-label={copy.recentAria(items.length)}
            >
                {copy.recent(items.length)}
            </button>
            {isOpen && (
                <div id={menuId} className={styles.recentMenu} role="menu" aria-label={copy.recentMenuAria}>
                    {items.map((item, index) => (
                        <button
                            key={item.key}
                            ref={element => {
                                itemRefs.current[index] = element;
                            }}
                            type="button"
                            role="menuitem"
                            className={[styles.recentItem, item.sku && styles.recentItemProduct, item.general && styles.recentItemGeneral]
                                .filter(Boolean)
                                .join(" ")}
                            onClick={() => {
                                close(true);
                                item.onChoose();
                            }}
                            onKeyDown={event => onItemKeyDown(event, index)}
                            disabled={disabled}
                            aria-label={item.ariaLabel}
                            title={item.title}
                        >
                            {item.general && <Chat20Regular className={styles.recentItemIcon} aria-hidden="true" />}
                            <span className={item.general || item.sku ? `${styles.recentItemLabel} ${styles.recentItemClamp}` : styles.recentItemLabel}>
                                {item.sku && <span className={styles.recentItemSku}>{`${item.sku} · `}</span>}
                                {item.label}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ProductThreadBarProps {
    // The product the host shows; null when it shows none.
    hostSku: string | null;
    // ENG-055: the product picked from "Recent", while it is the thread's product.
    pinnedSku: string | null;
    thread: ConversationThread;
    // The switch line; ENG-056: announced to screen readers only.
    notice: ThreadSwitchNotice | null;
    // ENG-055: other product threads with messages (utils/conversationThread.ts recentProductThreads).
    recent: RecentProductThread[];
    // ENG-056: the user's last question in the general chat, which names it; null without one.
    generalLastQuestion: string | null;
    // ENG-055: the title the thread registry remembers for a product.
    knownTitle: (sku: string | null) => string | undefined;
    siteId: SiteId;
    exampleLanguage: ThreadLanguage;
    // An answer is loading or streaming: switching waits until it ends.
    disabled: boolean;
    onChooseGeneral: () => void;
    onChooseProduct: () => void;
    onChooseHost: () => void;
    onChooseRecent: (sku: string) => void;
    // ENG-055: the chip learned its product's title (for the registry; no lookup of its own).
    onTitle: (sku: string, title: string) => void;
}

export const ProductThreadBar = ({
    hostSku,
    pinnedSku,
    thread,
    notice,
    recent,
    generalLastQuestion,
    knownTitle,
    siteId,
    exampleLanguage,
    disabled,
    onChooseGeneral,
    onChooseProduct,
    onChooseHost,
    onChooseRecent,
    onTitle
}: ProductThreadBarProps) => {
    // ENG-056: the open conversation as a chip, everything else in the "Recent" menu.
    const model = threadMenuModel(thread, hostSku, pinnedSku, recent);
    // The thread's product (pinned, else the host's): the product chip, or its row from the general
    // chat. The host's product while another one is pinned: the menu's "host" row.
    const productSku = pinnedSku ?? hostSku;
    const learnedTitle = useKnownProductTitle(productSku, siteId, knownTitle(productSku));
    const title = productSku ? learnedTitle || productSku : "";
    const hostRowSku = pinnedSku !== null && hostSku !== pinnedSku ? hostSku : null;
    const hostTitle = useProductTitle(hostRowSku, siteId, knownTitle(hostRowSku));

    useEffect(() => {
        if (productSku && learnedTitle) onTitle(productSku, learnedTitle);
    }, [productSku, learnedTitle, onTitle]);

    const { active } = model;
    const copy = threadCopy(exampleLanguage);
    // The general chat, named by its last question: the chat icon, then 40 characters…; the whole of it (up
    // to 200) as the tooltip and in the aria label.
    const generalMessage = threadMessageText(generalLastQuestion, GENERAL_THREAD_TITLE_MAX);
    const generalLabel = generalThreadLabel(generalLastQuestion, exampleLanguage);
    // ENG-058: the menu row keeps up to 90 characters and shows two lines; the chip stays at 40, one line.
    const generalRowLabel = generalThreadLabel(generalLastQuestion, exampleLanguage, GENERAL_THREAD_ROW_MAX);
    const generalTitle = generalMessage || copy.askMe;
    const generalAria = generalMessage ? copy.generalChatMessageAria(generalMessage) : copy.askMeAria;

    // ENG-058/068: the product chip is "{sku} · {title}" (the SKU and the dot in their own colour, no icon), or the SKU once.
    const chipParts = productRowParts(productSku ?? "", title);
    const chipName = productRowText(productSku ?? "", title);

    const rows = model.items.map((item): ThreadMenuRow => {
        if (item.kind === "general") {
            return { key: "general", label: generalRowLabel, general: true, title: generalTitle, ariaLabel: generalAria, onChoose: onChooseGeneral };
        }
        // ENG-058/068: every product row starts with its SKU; `name` is "{sku} · {title}", or the SKU once
        // (the lookups answer with the SKU when no title is known, which productRowParts takes as none).
        const row = (rowTitle: string | undefined, ariaLabel: (name: string) => string, onChoose: () => void): ThreadMenuRow => {
            const name = productRowText(item.sku, rowTitle);
            return {
                key: `${item.action}:${item.sku}`,
                ...productRowParts(item.sku, rowTitle),
                title: name,
                ariaLabel: ariaLabel(name),
                onChoose
            };
        };
        if (item.action === "product") return row(title, copy.aboutProductAria, onChooseProduct);
        if (item.action === "host") return row(hostTitle, copy.aboutHostAria, onChooseHost);
        return row(item.title, copy.recentItemAria, () => onChooseRecent(item.sku));
    });

    // The bar only while there is a menu row. The status region is always there (also without a
    // bar), so a screen reader announces the switch line when its text appears.
    return (
        <>
            {active && (
                <div className={styles.bar} data-print="hide">
                    <div className={styles.chipRow} role="group" aria-label={active.kind === "product" ? copy.productChipAria(chipName) : generalAria}>
                        <span className={styles.chip} title={active.kind === "product" ? chipName : generalTitle}>
                            {active.kind === "product" ? (
                                <>
                                    {chipParts.sku && <span className={styles.chipSku}>{chipParts.sku}</span>}
                                    {chipParts.sku && (
                                        <span className={styles.chipDot} aria-hidden="true">
                                            ·
                                        </span>
                                    )}
                                    <span className={styles.chipTitle}>{chipParts.label}</span>
                                </>
                            ) : (
                                <>
                                    <Chat20Regular className={styles.chipIcon} aria-hidden="true" />
                                    <span className={styles.chipTitle}>{generalLabel}</span>
                                </>
                            )}
                        </span>
                        <RecentThreadsMenu items={rows} exampleLanguage={exampleLanguage} disabled={disabled} />
                    </div>
                </div>
            )}
            <div className={styles.srOnly} role="status" aria-live="polite">
                {notice ? threadSwitchNoticeText(exampleLanguage, notice, title) : ""}
            </div>
        </>
    );
};

interface ProductThreadIntroProps {
    sku: string;
    siteId: SiteId;
    priceSource: "web" | "store";
    exampleLanguage: ThreadLanguage;
    disabled: boolean;
    // ENG-067: GET /n8n-config `readyQuestions`; null when it has none (or failed), undefined while it is loading.
    readyQuestions: ReadyQuestionsConfig | null | undefined;
    // ENG-067: the question's text and its intro_suggestion_id ("<group id>.<question id>").
    onSuggestion: (question: string, suggestionId: string) => void;
    // ENG-055: the intro learned the product's title (for the registry; no lookup of its own).
    onTitle: (sku: string, title: string) => void;
}

// Shown only while a product thread has no messages. When the details lookup fails the intro
// still shows, with the host title or the SKU and without image and price.
export const ProductThreadIntro = ({ sku, siteId, priceSource, exampleLanguage, disabled, readyQuestions, onSuggestion, onTitle }: ProductThreadIntroProps) => {
    const hostTitle = hostProductTitle(sku);
    // ENG-067: also set when the lookup fails (without data): the questions wait for it, then use the default group.
    const [details, setDetails] = useState<{ sku: string; data?: Record<string, any> } | null>(null);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setImageFailed(false);
        getProductDetails(sku, siteId, INTRO_DETAIL_FIELDS)
            .then(data => {
                if (!cancelled) setDetails({ sku, ...(data && typeof data === "object" ? { data } : {}) });
            })
            .catch(error => {
                console.debug("[product-thread] intro details lookup failed", { sku, error });
                if (!cancelled) setDetails({ sku });
            });
        return () => {
            cancelled = true;
        };
    }, [sku, siteId]);

    const data = details?.sku === sku ? details.data : undefined;
    const learnedTitle = hostTitle || normalizeHostContextField(data?.content, 500);
    const title = learnedTitle || sku;
    const brand = normalizeHostContextField(data?.brand, 200);
    const imageUrl = typeof data?.image_url === "string" && /^(https?:)?\/\//i.test(data.image_url) ? data.image_url : undefined;
    const price = introPrice(data?.pricing, priceSource);
    const copy = threadCopy(exampleLanguage);
    // ENG-067: once both the details and GET /n8n-config have answered, so the questions do not change under the user.
    const ready = details?.sku === sku && readyQuestions !== undefined ? readyQuestionsFor(readyQuestions, data?.sourcepage, exampleLanguage) : null;

    useEffect(() => {
        if (learnedTitle) onTitle(sku, learnedTitle);
    }, [sku, learnedTitle, onTitle]);

    return (
        <section className={styles.intro} aria-label={copy.introAria(title)}>
            <div className={styles.introProduct}>
                {imageUrl && !imageFailed && <img className={styles.introImage} src={imageUrl} alt={title} onError={() => setImageFailed(true)} />}
                <div className={styles.introProductText}>
                    {brand && <div className={styles.introBrand}>{brand}</div>}
                    <div className={styles.introTitle}>{title}</div>
                    {price && (
                        <div className={styles.introPrice}>
                            {formatEuro(price.price)}
                            {price.listPrice !== undefined && <s className={styles.introListPrice}>{formatEuro(price.listPrice)}</s>}
                        </div>
                    )}
                </div>
            </div>
            <p className={styles.introQuestion}>{copy.introQuestion(title)}</p>
            <div className={styles.introSuggestions} role="group" aria-label={copy.introSuggestionsAria} data-ready-group={ready?.groupId}>
                {ready?.questions.map(question => (
                    <button
                        key={question.id}
                        type="button"
                        className={styles.introSuggestion}
                        onClick={() => onSuggestion(question.text, question.id)}
                        disabled={disabled}
                        aria-label={copy.introSuggestionAria(question.text)}
                    >
                        {question.text}
                    </button>
                ))}
            </div>
        </section>
    );
};
